import { isElementDomNode } from 'remirror';

/*
 * nodeAtCoords : Returns the DOM node at given coords
 */
export function nodeAtCoords(coords, isDraggableBlock = () => false, targetType = 'drop') {
  const node = document.elementsFromPoint(coords.x, coords.y).find(
    (elem) =>
      // Root nodes are all draggable
      elem.parentNode.matches?.('.ProseMirror') ||
      elem.parentNode.matches?.('.column') ||
      // Nested nodes
      ((elem.previousElementSibling || targetType === 'drop') && isDraggableBlock(elem))
  );

  return isElementDomNode(node) ? node : null;
}

/*
 * nearestBlockFromCoords : When coords are inside the editor but
 * not directly over its content (e.g. if the cursor is inside the
 * padding area of the editor), returns the nearest node and the closest side.
 */
export function nearestBlockFromCoords(coords) {
  // ↓ There must be something better.
  const editorWrapper = document.querySelector('.ProseMirror');
  const firstEditorChildRect = editorWrapper.firstElementChild.getBoundingClientRect();
  const lastEditorChildRect = editorWrapper.lastElementChild.getBoundingClientRect();
  const editorContentRect = {
    top: firstEditorChildRect.top,
    right: firstEditorChildRect.right,
    left: firstEditorChildRect.left,
    bottom: lastEditorChildRect.bottom,
  };
  if (coords.x < editorContentRect.left) {
    return {
      nearestBlock: nodeAtCoords({ x: editorContentRect.left, y: coords.y }),
      side: 'left',
    };
  }
  if (coords.x > editorContentRect.right) {
    return {
      nearestBlock: nodeAtCoords({
        x: editorContentRect.right - 1,
        y: coords.y,
      }),
      side: 'right',
    };
  }
  if (coords.y < editorContentRect.top) {
    return {
      nearestBlock: nodeAtCoords({ x: coords.x, y: editorContentRect.top }),
      side: 'top',
    };
  }
  if (coords.y > editorContentRect.bottom) {
    return {
      nearestBlock: nodeAtCoords({
        x: coords.x,
        y: editorContentRect.bottom - 1,
      }),
      side: 'bottom',
    };
  }

  return { nearestBlock: null };
}

/*
 * nodePosAtDOM : Returns the node position of a given DOM node
 */
export function nodePosAtDOM(node, view) {
  const pos = view.posAtDOM(node);
  const resolvedPos = view.state.doc.resolve(pos);
  const nodePos = resolvedPos.path[3 * resolvedPos.depth - 1];

  return nodePos;
}

/*
 * getClosestSide : Returns the DOM node side (top, right, left, bottom)
 * which is the closest to the given coords.
 */
export function getClosestSide(node, coords) {
  const nodeRect = node.getBoundingClientRect();
  const distances = [
    { origin: 'left', distance: Math.abs(coords.x - nodeRect.left) },
    { origin: 'right', distance: Math.abs(coords.x - nodeRect.right) },
    { origin: 'top', distance: Math.abs(coords.y - nodeRect.top) },
    { origin: 'bottom', distance: Math.abs(coords.y - nodeRect.bottom) },
  ];
  const shortestDistance = distances.sort((a, b) => a.distance - b.distance)[0];

  return shortestDistance.origin;
}

/*
 * isNodeSlice : Returns true if the given slice represents a node,
 * otherwise returns false.
 */
export function isNodeSlice(slice) {
  return slice.openStart === 0 && slice.openEnd === 0 && slice.content.childCount === 1;
}

/*
 * createColumnsNodeWith : Creates a new Columns node with the given content
 */
export function createColumnsNodeWith(content, nodeTypes) {
  const columns = [];
  content.forEach((node) => {
    columns.push(
      nodeTypes.column.createChecked(
        { scale: 1 / content.length },
        node.type.name === 'column' ? [...node.content.content] : node
      )
    );
  });

  return nodeTypes.columns.create(null, columns);
}
